<script setup lang="ts">
/**
 * Presentational row for a single saved (read-only state) highlight.
 *
 * Styled to the Figma RAW redesign (node 1791:99905): a bordered, rounded "Draggable item"
 * row with a drag grip, a purple index badge, the text (2-line clamp + Read more when it
 * overflows) and trailing Edit (custom only) + Delete icon buttons separated by a divider.
 *
 * A DUMB row: props down / events up, NO composable and NO store access.
 * All gating comes in as plain props computed by the parent editor:
 * - `readonly`  — the whole editor is read-only; hide every action.
 * - `locked`    — a DIFFERENT row is currently being edited; disable this row's actions.
 * - `editable`  — this row supports inline editing (custom items only; premade do not).
 * The row never decides policy; it only renders controls and emits intent.
 *
 * The one piece of logic that lives here (by design) is the READ-MORE overflow detection: it
 * is a DOM-bound, presentational concern (needs the mounted element + a ResizeObserver).
 */
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from "vue";
import type { HighlightItem } from "@/features/experience-shared/highlights-v2/types/highlight-item";
import DiffText from "@/features/experience-shared/components/DiffText.vue";
import NovaIcon from "@/ui-kit/NovaIcon/NovaIcon.vue";
import NovaButtonIcon from "@/ui-kit/NovaButtonIcon/NovaButtonIcon.vue";
import NovaButton from "@/ui-kit/NovaButton/NovaButton.vue";

const props = defineProps<{
  item: HighlightItem;
  /** 1-based number shown in the purple index badge. */
  index: number;
  /** Whole editor is read-only — hides every action. */
  readonly?: boolean;
  /** A different row is being edited — disable this row's actions. */
  locked?: boolean;
  /** This row supports inline editing (custom only). */
  editable?: boolean;
}>();

const emit = defineEmits<{
  (e: "edit"): void;
  (e: "remove"): void;
}>();

/** Actions are hidden in readonly and disabled while another row holds the edit lock. */
const actionsDisabled = computed(() => Boolean(props.readonly) || Boolean(props.locked));
const canEdit = computed(() => Boolean(props.editable) && !actionsDisabled.value);
const canDelete = computed(() => !actionsDisabled.value);
/**
 * The drag handle is HIDDEN only when the whole editor is read-only. While another row holds
 * the edit lock the handle stays VISIBLE but DISABLED (the list itself blocks the drag), so
 * the row keeps its shape and the user can see reordering is temporarily unavailable.
 */
const showReorderHandle = computed(() => !props.readonly);
/** Reordering is disabled (handle dimmed, no grab cursor) while another row is being edited. */
const reorderDisabled = computed(() => Boolean(props.locked));
/** Whether the trailing action cluster (divider + buttons) is shown at all. */
const hasActions = computed(() => canEdit.value || canDelete.value);

// --- Read-more overflow detection (presentational, lives here on purpose) -----------------
// The saved item is clamped to 2 lines (`line-clamp-2`). We only show the read-more toggle
// when the text actually overflows those 2 lines, detected by comparing scrollHeight vs
// clientHeight while clamped. A ResizeObserver re-checks on width changes (responsive), and
// we re-check when the text changes. Toggling "expanded" removes the clamp.
const textEl = ref<HTMLElement | null>(null);
const isExpanded = ref(false);
const isOverflowing = ref(false);

function measureOverflow(): void {
  const el = textEl.value;
  if (el == null) {
    isOverflowing.value = false;
    return;
  }
  // Only meaningful while clamped; when expanded the clamp is off so scrollHeight === clientHeight.
  if (isExpanded.value) {
    return;
  }
  isOverflowing.value = el.scrollHeight > el.clientHeight;
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  measureOverflow();
  if (typeof ResizeObserver !== "undefined" && textEl.value != null) {
    resizeObserver = new ResizeObserver(() => measureOverflow());
    resizeObserver.observe(textEl.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});

// Re-measure when the rendered text changes and reset the expand state.
watch(
  () => [props.item.name, props.item.diff?.oldValue, props.item.diff?.newValue],
  () => {
    isExpanded.value = false;
    // wait for the DOM to reflect the new text before measuring
    nextTick(measureOverflow);
  }
);

function toggleExpanded(): void {
  isExpanded.value = !isExpanded.value;
  if (!isExpanded.value) {
    nextTick(measureOverflow);
  }
}
</script>

<template>
  <div
    class="flex w-full items-start gap-2 rounded-lg border border-neutral-60 bg-white p-2"
    data-testid="highlight-list-item"
    :data-kind="item.kind"
  >
    <div class="flex min-w-0 flex-1 items-start justify-center gap-2 self-stretch">
      <!-- Leading grip + index cluster -->
      <div class="flex shrink-0 items-center gap-1">
        <!-- Drag handle: hidden only when readonly; visible-but-disabled while another row is
             being edited. The `drag-handle` class (NovaSortableList's grip target) and grab
             cursor are only applied when reordering is actually enabled. -->
        <span
          v-if="showReorderHandle"
          class="flex rotate-90 items-center justify-center rounded-xs text-text-90"
          :class="reorderDisabled ? 'cursor-not-allowed opacity-40' : 'drag-handle cursor-grab hover:bg-primary-10'"
          data-testid="highlight-drag-handle"
          :aria-disabled="reorderDisabled || undefined"
          aria-hidden="true"
        >
          <NovaIcon name="grip-dots" :size="14" />
        </span>
        <!-- Index badge: purple when editable, gray when the whole editor is read-only -->
        <span
          class="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-center font-bold text-[12px] leading-4"
          :class="readonly ? 'bg-neutral-60 text-text-90' : 'bg-secondary-20 text-secondary-80'"
          data-testid="highlight-index"
        >
          {{ index }}
        </span>
      </div>

      <div class="flex min-h-5 min-w-0 flex-1 flex-col justify-center gap-0.5">
        <p
          ref="textEl"
          class="m-0 whitespace-pre-line break-words text-[14px] leading-[19px] text-text-100"
          :class="{ 'line-clamp-2': !isExpanded }"
          data-testid="highlight-text"
        >
          <DiffText v-if="item.diff" :value="item.diff.newValue" :old-value="item.diff.oldValue" />
          <template v-else>{{ item.name }}</template>
        </p>
        <NovaButton
          v-if="isOverflowing || isExpanded"
          variant="action"
          size="xxs"
          theme="primary"
          class="self-start"
          data-testid="highlight-read-more"
          @click="toggleExpanded"
        >
          {{ isExpanded ? $t("common.read-less") : $t("common.read-more") }}
          <NovaIcon :name="isExpanded ? 'chevron-up' : 'chevron-down'" :size="12" class="ml-1" />
        </NovaButton>
      </div>
    </div>

    <div v-if="hasActions" class="flex h-5 shrink-0 items-center gap-2">
      <span class="h-[18px] w-px bg-neutral-60" aria-hidden="true"></span>
      <NovaButtonIcon
        v-if="canEdit"
        name="edit"
        theme="primary"
        shape="square"
        :size="16"
        :aria-label="$t('common.edit')"
        data-testid="highlight-edit"
        @click="emit('edit')"
      />
      <NovaButtonIcon
        v-if="canDelete"
        name="trash"
        theme="primary"
        shape="square"
        :size="16"
        :aria-label="$t('common.delete')"
        data-testid="highlight-delete"
        @click="emit('remove')"
      />
    </div>
  </div>
</template>
